import { useRef, useEffect } from 'react';
import { select } from 'd3-selection';
import type { D3DragEvent } from 'd3-drag';
import { drag as Drag } from 'd3-drag';

type Position = {
  x: number;
  y: number;
};

type Props = {
  children: React.ReactNode;
  className?: string;
  position: Position;
  onDragStart?: (e: React.MouseEvent<SVGGElement>) => void;
  onDrag(context: DragContext): void;
  onDragEnd?: (context: DragContext) => void;
  onDragMoveStarted?: (e: React.MouseEvent<SVGGElement>) => void;
  onDragMoveEnded?: (context: DragContext) => void;
  onClick?: (e: React.MouseEvent<SVGGElement>) => void;
  onContextMenu?: (e: React.MouseEvent<SVGGElement>) => void;
  shouldSuppressDrag?: (e: MouseEvent) => boolean;
};

export type DragContext = {
  dragStartX: number;
  dragStartY: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  nativeEvent: MouseEvent;
};

export const DraggableSVGGElement = (props: Props) => {
  const ref = useRef<SVGGElement | null>(null);
  const propsRef = useRef<Props>(props);
  propsRef.current = props;

  useEffect(() => {
    const g = ref.current;
    if (!g) {
      return;
    }

    const dragState: {
      dragMoved: boolean;
      suppressed: boolean;
      dragStartX: number;
      dragStartY: number;
      // mousedown 時点のクライアント座標と、ドラッグコンテナ（g の親要素）の CTM 逆行列を保存する。
      // ドラッグ中に RTDB 位置更新で g.transform が変わっても正確な座標計算を維持するため、
      // mousedown 時点の CTM を固定してマウス移動量（delta）を計算する。
      // g 自身ではなく親要素の CTM を使うのは、座標系を D3 の event.x/y と同じ
      // コンテナ座標（ビュー座標）に保つため。g 自身の CTM だと要素ローカル座標になり、
      // 絶対座標を参照する処理（ElementDragManager.findDropTargetView など）が壊れる。
      startClientX: number;
      startClientY: number;
      containerCTMInverse: DOMMatrix | null;
      // dx/dy を getPos() ベースで計算するために、直前イベントの x/y を保持する。
      prevX: number;
      prevY: number;
    } = {
      dragMoved: false,
      suppressed: false,
      dragStartX: 0,
      dragStartY: 0,
      startClientX: 0,
      startClientY: 0,
      containerCTMInverse: null,
      prevX: 0,
      prevY: 0,
    };

    // MouseEvent または TouchEvent から clientX/Y を安全に取得する。
    // D3 の sourceEvent は TouchEvent になる場合があり、その場合 clientX/Y は touches から取得する。
    const getClientXY = (sourceEvent: Event): { clientX: number; clientY: number } | null => {
      if (sourceEvent instanceof MouseEvent) {
        return { clientX: sourceEvent.clientX, clientY: sourceEvent.clientY };
      }
      // デスクトップブラウザ・テスト環境では TouchEvent が未定義の場合があるため typeof でガードする
      if (typeof TouchEvent !== 'undefined' && sourceEvent instanceof TouchEvent) {
        const touch = sourceEvent.touches[0] ?? sourceEvent.changedTouches[0];
        if (touch) return { clientX: touch.clientX, clientY: touch.clientY };
      }
      return null;
    };

    // clientX/Y (スクリーン座標) を、mousedown 時と同じコンテナ座標系の位置に変換する。
    // D3 が内部管理する event.x/y は、ドラッグ中に RTDB 位置更新で g.transform が変わると不正確になる。
    // mousedown 時点のコンテナの CTM を固定し、スクリーン座標の移動量から一貫した座標を計算する。
    const getPos = (event: D3DragEvent<Element, unknown, unknown>): { x: number; y: number } => {
      const { containerCTMInverse, startClientX, startClientY, dragStartX, dragStartY } = dragState;
      const client = getClientXY(event.sourceEvent);
      if (containerCTMInverse && client) {
        const ptStart = new DOMPoint(startClientX, startClientY).matrixTransform(containerCTMInverse);
        const ptCurrent = new DOMPoint(client.clientX, client.clientY).matrixTransform(containerCTMInverse);
        return {
          x: dragStartX + (ptCurrent.x - ptStart.x),
          y: dragStartY + (ptCurrent.y - ptStart.y),
        };
      }
      // containerCTMInverse が取得できなかった場合のフォールバック（通常は発生しない）
      console.warn('DraggableSVGGElement: containerCTMInverse is null, falling back to D3 event coordinates');
      return { x: event.x, y: event.y };
    };

    const drag = Drag<SVGGElement, unknown, unknown>()
      .on('start', (event: D3DragEvent<SVGGElement, unknown, unknown>) => {
        // shouldSuppressDrag は MouseEvent 前提（button 等）の実装が多いため、
        // MouseEvent のときだけ評価し、タッチ操作時は抑制しない（false）扱いにする。
        const shouldSuppress =
          event.sourceEvent instanceof MouseEvent
            ? (propsRef.current.shouldSuppressDrag?.(event.sourceEvent) ?? false)
            : false;

        // D3 のドラッグコンテナ（g の親要素）の CTM を使う。g 自身の CTM は要素位置の
        // translate を含むため、要素ローカル座標になってしまい絶対座標の意味が変わる。
        const container = g.parentNode instanceof SVGGraphicsElement ? g.parentNode : null;
        const containerCTMInverse = container?.getScreenCTM()?.inverse() ?? null;
        const client = getClientXY(event.sourceEvent);
        // dragStartX/Y も CTM ベースで計算し、getPos() と同じ座標系に統一する。
        // containerCTMInverse または client が取得できない場合のみ D3 の event.x/y にフォールバックする。
        let dragStartX = event.x;
        let dragStartY = event.y;
        if (containerCTMInverse && client) {
          const pt = new DOMPoint(client.clientX, client.clientY).matrixTransform(containerCTMInverse);
          dragStartX = pt.x;
          dragStartY = pt.y;
        }
        Object.assign(dragState, {
          dragMoved: false,
          suppressed: shouldSuppress,
          dragStartX,
          dragStartY,
          startClientX: client?.clientX ?? 0,
          startClientY: client?.clientY ?? 0,
          // ドラッグ中に RTDB 位置更新で g.transform が変わっても座標が狂わないよう、
          // mousedown 時点のコンテナの CTM（zoom/pan も含む）を記録する
          containerCTMInverse,
          prevX: dragStartX,
          prevY: dragStartY,
        });

        if (!shouldSuppress) {
          propsRef.current.onDragStart?.(event.sourceEvent);
        }
      })
      .on('drag', (event: D3DragEvent<Element, unknown, unknown>) => {
        if (dragState.suppressed) {
          dragState.dragMoved = true;
          return;
        }

        if (!dragState.dragMoved) {
          dragState.dragMoved = true;
          propsRef.current.onDragMoveStarted?.(event.sourceEvent);
        }

        const { x, y } = getPos(event);
        const dx = x - dragState.prevX;
        const dy = y - dragState.prevY;
        dragState.prevX = x;
        dragState.prevY = y;
        propsRef.current.onDrag?.({
          dragStartX: dragState.dragStartX,
          dragStartY: dragState.dragStartY,
          x,
          y,
          dx,
          dy,
          nativeEvent: event.sourceEvent,
        });
      })
      .on('end', (event: D3DragEvent<Element, unknown, unknown>) => {
        if (dragState.suppressed) {
          if (dragState.dragMoved) {
            // d3がclickイベントを抑制するため、onClickを手動で呼び出す
            propsRef.current.onClick?.(event.sourceEvent as React.MouseEvent<SVGGElement>);
          }
          // 移動なしの場合はnative clickが正常に発火するため、手動呼び出し不要
          dragState.suppressed = false;
          dragState.dragMoved = false;
          return;
        }

        const { x, y } = getPos(event);
        const context: DragContext = {
          dragStartX: dragState.dragStartX,
          dragStartY: dragState.dragStartY,
          x,
          y,
          dx: x - dragState.prevX,
          dy: y - dragState.prevY,
          nativeEvent: event.sourceEvent,
        };
        if (dragState.dragMoved) {
          propsRef.current.onDragMoveEnded?.(context);
        }

        propsRef.current.onDragEnd?.(context);
      });

    select(g).call(drag);

    return () => {
      drag.on('drag', null).on('start', null).on('end', null);
      select(g).on('.drag', null);
    };
  }, []);

  return (
    <g
      className={props.className}
      ref={ref}
      transform={`translate(${props.position.x}, ${props.position.y})`}
      onClick={props.onClick}
      onContextMenu={props.onContextMenu}
    >
      {props.children}
    </g>
  );
};
